import { useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiErrorDetail, getLimitDetail, newClientRequestId, friendlyGenerationError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const FAITHS = ["Christian", "Muslim", "Hindu", "Buddhist", "Jewish", "Sikh", "Secular", "Other"];
const ENGLISH_LEVELS = ["Simple", "Medium", "Rich"];
const TONES = ["Reverent", "Warm", "Poetic", "Child-friendly"];
const LENGTHS = ["Short", "Medium", "Long"];
// Lord Ram MUST appear above Hanuman (Hanuman is Ram's devotee, never above him).
const HINDU_DEITIES = [
  "Shiva",
  "Lord Ram",
  "Vishnu / Narayana",
  "Venkateswara / Balaji",
  "Krishna",
  "Devi / Durga",
  "Lakshmi",
  "Ganesha",
  "Hanuman",
  "Saraswati",
  "Kali Ma",
  "Murugan",
  "Ayyappa",
  "Family Deity (Kuladevata)",
  "Village Deity (Gramadevata)",
  "Universal Divine / Brahman",
  "I am not sure",
];

const BASE_STEPS = [
  { key: "faith", title: "Which faith tradition speaks to you?", subtitle: "We honor each with care and precision.", type: "chips", options: FAITHS },
  { key: "region", title: "Where do you call home?", subtitle: "Country or region. This shapes the cadence and references.", type: "text", placeholder: "e.g. India, Egypt, USA" },
  { key: "place", title: "And a particular place?", subtitle: "Optional. A state, city, or sacred site.", type: "text", placeholder: "e.g. Kerala, Cairo, Boston", optional: true },
  { key: "denomination", title: "A denomination or tradition?", subtitle: "Optional. e.g. Marthomite, Roman Catholic, Sunni, Vaishnava, Reform.", type: "text", placeholder: "Leave blank if unsure", optional: true },
  { key: "goals", title: "What do you wish to pray for?", subtitle: "One or more intentions. Be as specific as you'd like.", type: "textarea", placeholder: "e.g. peace of mind, safe journey for my mother, abundance in my work" },
  { key: "names", title: "Any names to mention?", subtitle: "Optional. We will weave them in with reverence.", type: "text", placeholder: "e.g. Maria, Daniel", optional: true },
  { key: "english_level", title: "How should the words feel?", subtitle: "Pick a vocabulary that comes naturally to you.", type: "chips", options: ENGLISH_LEVELS },
  { key: "tone", title: "And the tone?", subtitle: "The emotional register of the prayer.", type: "chips", options: TONES },
  { key: "length", title: "How long?", subtitle: "From a brief blessing to a longer meditation.", type: "chips", options: LENGTHS },
];

const HINDU_DEITY_STEP = {
  key: "deity",
  title: "Which form of the Divine do you feel close to?",
  subtitle: "For this prayer. Each deity carries a different felt presence.",
  type: "chips",
  options: HINDU_DEITIES,
};
const HINDU_REGIONAL_STEP = {
  key: "regional_name",
  title: "Any regional name you prefer?",
  subtitle: "Optional. e.g. Kannan, Guruvayurappan, Pillaiyar, Perumal, Vel Murugan. Leave blank to use the traditional name.",
  type: "text",
  placeholder: "Leave blank if unsure",
  optional: true,
};

function buildSteps(faith) {
  if (faith !== "Hindu") return BASE_STEPS;
  // Insert deity + regional name AFTER denomination (index 3), so order is:
  // faith → region → place → denomination → deity → regional_name → goals → names → english_level → tone → length
  const out = [...BASE_STEPS];
  out.splice(4, 0, HINDU_DEITY_STEP, HINDU_REGIONAL_STEP);
  return out;
}

export default function Builder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [inputs, setInputs] = useState({
    faith: "", faith_other: "", region: "", place: "", denomination: "",
    deity: "", regional_name: "",
    goals: "", names: "", english_level: "Medium", tone: "Reverent", length: "Medium",
  });
  const [question, setQuestion] = useState(null); // {question, options}
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [limitBlock, setLimitBlock] = useState(null); // {code, message, signup_required}
  // Stable client_request_id for the current submission — re-used if user mashes the
  // button (backend de-dupes via idempotency cache). New ID per actual submission.
  const requestIdRef = useRef(null);

  const STEPS = useMemo(() => buildSteps(inputs.faith), [inputs.faith]);
  const safeIdx = Math.min(stepIdx, STEPS.length - 1);
  const step = STEPS[safeIdx];
  const isLast = safeIdx === STEPS.length - 1;

  const setVal = (key, v) => setInputs((s) => ({ ...s, [key]: v }));

  const canAdvance = () => {
    const v = (inputs[step.key] || "").toString().trim();
    if (step.key === "faith" && inputs.faith === "Other") {
      return (inputs.faith_other || "").trim().length > 0;
    }
    return step.optional || v.length > 0;
  };

  const next = async () => {
    if (!canAdvance()) return;
    if (!isLast) {
      setStepIdx((i) => i + 1);
      return;
    }
    if (busy) return; // double-click guard (also enforced by disabled state)
    // Generate
    setBusy(true);
    setError("");
    setLimitBlock(null);
    if (!requestIdRef.current) requestIdRef.current = newClientRequestId();
    try {
      const { data } = await api.post("/prayer/turn", {
        inputs,
        client_request_id: requestIdRef.current,
      });
      setSessionId(data.session_id);
      if (data.type === "question") {
        setQuestion({ question: data.question, options: data.options || [] });
        // A new request_id for the answer step
        requestIdRef.current = newClientRequestId();
      } else if (data.type === "prayer") {
        const payload = {
          session_id: data.session_id,
          title: data.title,
          body: data.body,
          closing: data.closing,
          tradition_notes: data.tradition_notes,
          mantra: data.mantra,
          repetition: data.repetition,
          special_day: data.special_day,
          journey: data.journey,
          inputs,
        };
        sessionStorage.setItem("inlight_current_prayer", JSON.stringify(payload));
        navigate("/prayer");
      }
    } catch (e) {
      const limit = getLimitDetail(e);
      if (limit) {
        setLimitBlock(limit);
      } else {
        setError(friendlyGenerationError(e));
      }
      // Allow user to retry with a fresh ID after an error
      requestIdRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const answerClarification = async (optionText) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setLimitBlock(null);
    if (!requestIdRef.current) requestIdRef.current = newClientRequestId();
    try {
      const { data } = await api.post("/prayer/turn", {
        session_id: sessionId,
        answer: optionText,
        client_request_id: requestIdRef.current,
      });
      if (data.type === "question") {
        setQuestion({ question: data.question, options: data.options || [] });
        requestIdRef.current = newClientRequestId();
      } else if (data.type === "prayer") {
        const payload = {
          session_id: data.session_id,
          title: data.title,
          body: data.body,
          closing: data.closing,
          tradition_notes: data.tradition_notes,
          mantra: data.mantra,
          repetition: data.repetition,
          special_day: data.special_day,
          journey: data.journey,
          inputs,
        };
        sessionStorage.setItem("inlight_current_prayer", JSON.stringify(payload));
        navigate("/prayer");
      }
    } catch (e) {
      const limit = getLimitDetail(e);
      if (limit) {
        setLimitBlock(limit);
      } else {
        setError(friendlyGenerationError(e));
      }
      requestIdRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  // Calm limit-reached screen (full screen overlay — sacred, not technical)
  if (limitBlock) {
    const isGuestSignup = !!limitBlock.signup_required;
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <Header />
        <div className="relative max-w-xl mx-auto px-6 pt-28 md:pt-36 pb-20 text-center fade-up" data-testid="limit-block-screen">
          <p className="text-[11px] tracking-[0.35em] uppercase mb-8" style={{ color: "var(--sanctuary-ink-muted)" }}>
            A quiet pause
          </p>
          <div
            aria-hidden="true"
            className="mx-auto mb-10"
            style={{ width: "44px", height: "1px", backgroundColor: "rgba(203,161,83,0.55)" }}
          />
          <h2
            className="font-serif-display text-3xl md:text-4xl leading-snug mb-8 tracking-tight"
            style={{ color: "var(--sanctuary-ink)" }}
            data-testid="limit-block-message"
          >
            {limitBlock.message}
          </h2>
          <div className="mt-10 flex flex-col items-center gap-4">
            {isGuestSignup ? (
              <>
                <Link
                  to="/register"
                  data-testid="limit-block-signup"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full btn-primary text-base"
                >
                  Create a free account
                </Link>
                <Link
                  to="/login"
                  data-testid="limit-block-signin"
                  className="text-sm underline"
                  style={{ color: "var(--sanctuary-ink-soft)" }}
                >
                  I already have one
                </Link>
              </>
            ) : (
              <Link
                to="/"
                data-testid="limit-block-home"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base"
                style={{
                  color: "var(--sanctuary-ink)",
                  background: "rgba(255,255,255,0.7)",
                  border: "1px solid #E6E0D2",
                }}
              >
                Return to the sanctuary
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Clarifying-question screen
  if (question) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <Header />
        <div className="sanctuary-glow absolute inset-x-0 top-16 h-[520px] -z-0 pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6 pt-20 md:pt-28 pb-20">
          <p className="text-xs tracking-[0.25em] uppercase mb-6" style={{ color: "var(--sanctuary-gold)" }}>
            A gentle question
          </p>
          <h2
            className="font-serif-display text-3xl md:text-4xl leading-snug mb-10 tracking-tight"
            style={{ color: "var(--sanctuary-ink)" }}
            data-testid="clarify-question-text"
          >
            {question.question}
          </h2>
          <div className="flex flex-wrap gap-3" data-testid="clarify-options">
            {question.options.map((opt, i) => (
              <button
                key={i}
                disabled={busy}
                onClick={() => answerClarification(opt)}
                data-testid={`clarify-option-${i}`}
                className="chip px-5 py-3 rounded-full text-sm md:text-base disabled:opacity-50"
                style={{ color: "var(--sanctuary-ink)" }}
              >
                {opt}
              </button>
            ))}
          </div>
          {busy && (
            <p data-testid="clarify-busy" className="mt-10 font-serif-display italic" style={{ color: "var(--sanctuary-ink-muted)" }}>
              Writing in the cadence of your tradition…
            </p>
          )}
          {error && <p data-testid="clarify-error" className="mt-6 text-sm" style={{ color: "var(--sanctuary-alert)" }}>{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
      <Header />
      <div className="sanctuary-glow absolute inset-x-0 top-16 h-[520px] -z-0 pointer-events-none" />

      <div className="relative max-w-2xl mx-auto px-6 pt-16 md:pt-24 pb-20">
        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-12" data-testid="builder-progress">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className="h-[2px] flex-1 rounded-full transition-all"
              style={{ backgroundColor: i <= stepIdx ? "var(--sanctuary-accent)" : "var(--sanctuary-border)" }}
            />
          ))}
        </div>

        <div key={step.key} className="fade-up">
          <p className="text-xs tracking-[0.25em] uppercase mb-5" style={{ color: "var(--sanctuary-gold)" }} data-testid="builder-step-eyebrow">
            Step {stepIdx + 1} of {STEPS.length}
          </p>
          <h2
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-4 tracking-tight"
            style={{ color: "var(--sanctuary-ink)" }}
            data-testid="builder-step-title"
          >
            {step.title}
          </h2>
          <p className="text-base md:text-lg mb-12" style={{ color: "var(--sanctuary-ink-soft)" }} data-testid="builder-step-subtitle">
            {step.subtitle}
          </p>

          {step.type === "chips" && (
            <div className="flex flex-wrap gap-3" data-testid={`builder-chips-${step.key}`}>
              {step.options.map((opt) => {
                const selected = inputs[step.key] === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setVal(step.key, opt)}
                    data-testid={`builder-chip-${step.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                    className="px-5 py-3 rounded-full text-sm md:text-base transition"
                    style={{
                      backgroundColor: selected ? "var(--sanctuary-accent)" : "white",
                      color: selected ? "white" : "var(--sanctuary-ink)",
                      border: `1px solid ${selected ? "var(--sanctuary-accent)" : "var(--sanctuary-border)"}`,
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {step.key === "faith" && inputs.faith === "Other" && (
            <div className="mt-8 fade-up" data-testid="faith-other-field">
              <input
                autoFocus
                type="text"
                value={inputs.faith_other}
                onChange={(e) => setVal("faith_other", e.target.value)}
                placeholder="Tell us the tradition your prayer should honor"
                data-testid="builder-input-faith_other"
                className="input-line text-lg md:text-xl"
              />
              <p className="mt-3 text-sm italic" style={{ color: "var(--sanctuary-ink-muted)" }} data-testid="faith-other-helper">
                We'll do our best to honor it with care.
              </p>
            </div>
          )}

          {step.type === "text" && (
            <input
              autoFocus
              type="text"
              value={inputs[step.key]}
              onChange={(e) => setVal(step.key, e.target.value)}
              placeholder={step.placeholder}
              data-testid={`builder-input-${step.key}`}
              className="input-line text-xl md:text-2xl"
            />
          )}

          {step.type === "textarea" && (
            <textarea
              autoFocus
              value={inputs[step.key]}
              onChange={(e) => setVal(step.key, e.target.value)}
              placeholder={step.placeholder}
              rows={3}
              data-testid={`builder-textarea-${step.key}`}
              className="input-line text-xl md:text-2xl resize-none"
            />
          )}
        </div>

        {error && (
          <p data-testid="builder-error" className="mt-6 text-sm" style={{ color: "var(--sanctuary-alert)" }}>
            {error}
          </p>
        )}

        <div className="mt-16 flex items-center justify-between">
          <button
            onClick={back}
            disabled={stepIdx === 0}
            data-testid="builder-back-button"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full disabled:opacity-30 transition hover-paper"
            style={{ color: "var(--sanctuary-ink-soft)" }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back
          </button>

          <button
            onClick={next}
            disabled={!canAdvance() || busy}
            data-testid="builder-next-button"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-primary text-base disabled:opacity-50"
          >
            {busy ? (
              <>
                <Sparkles className="w-4 h-4 animate-pulse" strokeWidth={1.5} />
                Composing…
              </>
            ) : isLast ? (
              <>
                Compose my prayer
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
